import { Button, Group } from '@mantine/core';
import React, { useRef, useState, useEffect } from 'react';
import {
    useAddComment,
    useMentionableUsers,
} from '@/features/comment/services/useCommentQueries';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import MentionableEditor, { MentionableEditorRef } from './MentionableEditor';
import { convertJsonToPlainTextWithMentions } from '../utils/mentionUtils';
import { CommentMention } from '@/common/types/databaseTypes';

interface CommentFormProps {
    parentId?: string;
    targetId: string;
    targetType: 'goal' | 'todo' | 'note' | 'feedback' | 'daily_scrum';
    onCommentAdded?: () => void;
}

export const CommentForm: React.FC<CommentFormProps> = ({
    parentId,
    targetId,
    targetType,
    onCommentAdded,
}) => {
    const { t } = useTranslation('comment');
    const { user } = useAuthStore();
    const [comment, setComment] = useState('');
    const addCommentMutation = useAddComment(
        targetType as 'goal' | 'todo' | 'note'
    );
    const { data: mentionableUsers, isLoading: loadingUsers } =
        useMentionableUsers(targetType, targetId);
    const [mentionedUsers, setMentionedUsers] = useState<CommentMention[]>([]);

    const editorRef = useRef<MentionableEditorRef>(null);

    useEffect(() => {
        if (addCommentMutation.isSuccess && onCommentAdded) {
            onCommentAdded();
        }
    }, [addCommentMutation.isSuccess, onCommentAdded]);

    const handleFileSelect = (file: File) => {
        console.log('Selected file:', file.name);
        // TODO: File Upload
    };

    // 멘션 정보 추출 및 전송
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (comment.trim() && targetId && user?.id) {
            if (comment.trim() && targetId && user?.id) {
                const params = {
                    content: comment.trim(),
                    register_id: user.id,
                    parent_id: parentId,
                };

                // targetType에 따라 적절한 파라미터 추가
                if (targetType === 'goal') {
                    addCommentMutation.mutate({
                        ...params,
                        goal_id: targetId,
                        todo_id: null,
                        note_id: null,
                        mentions: mentionedUsers,
                    });
                } else if (targetType === 'todo') {
                    addCommentMutation.mutate({
                        ...params,
                        goal_id: null,
                        todo_id: targetId,
                        note_id: null,
                        mentions: mentionedUsers,
                    });
                } else {
                    // note 타입인 경우
                    addCommentMutation.mutate({
                        ...params,
                        goal_id: null,
                        todo_id: null,
                        note_id: targetId,
                        mentions: mentionedUsers,
                    });
                }

                // 상태 초기화
                setComment('');
                setMentionedUsers([]);
                editorRef.current?.clearContent();
            }
        }
    };

    return (
        <Group
            component="form"
            gap="xs"
            ml={parentId ? 22 : 0}
            mt={parentId ? 12 : 0}
            onSubmit={handleSubmit}
        >
            <MentionableEditor
                ref={editorRef}
                mentionableUsers={mentionableUsers || []}
                loadingUsers={loadingUsers}
                onChange={(content, mentions, json) => {
                    // setComment(content);
                    setMentionedUsers(
                        mentions.map(mention => ({
                            user_id: mention.id,
                            user_name: mention.label,
                        }))
                    );
                    const plainTextWithMentions =
                        convertJsonToPlainTextWithMentions(json);
                    setComment(plainTextWithMentions);
                }}
                autoFocus={parentId ? true : false}
            />
            <Button
                type="submit"
                variant="filled"
                size="sm"
                radius="md"
                disabled={
                    !comment.trim() || addCommentMutation.isPending || !user
                }
            >
                {t('common:action.enter')}
            </Button>
        </Group>
    );
};
